// src/components/UploadToolbar.js
import { Typography, Card, CardActionArea, Button, Stack } from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import { Image } from "fabric";
import { useData } from "../hooks/useData";

const UploadToolbar = ({ canvas }) => {
  const { canvasScale, setSnackbar } = useData();

  const handleAddImage = (event) => {
    const imgObj = event.target.files[0];
    console.log("UploadToolbar: File selected", {
      fileName: imgObj?.name,
      fileType: imgObj?.type,
      canvasAvailable: !!canvas,
      canvasScale,
    });

    if (!imgObj || !canvas || !canvasScale) {
      setSnackbar({
        open: true,
        message: "No file selected, canvas not initialized, or scale missing.",
        severity: "error",
      });
      console.error("UploadToolbar: Cannot add image", {
        imgObj: !!imgObj,
        canvas: !!canvas,
        canvasScale,
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const imageURL = e.target.result;
      console.log("UploadToolbar: FileReader loaded", {
        imageURLLength: imageURL?.length,
      });

      const imageElement = document.createElement("img");
      imageElement.src = imageURL;
      imageElement.onload = () => {
        console.log("UploadToolbar: Image element loaded", {
          naturalWidth: imageElement.naturalWidth,
          naturalHeight: imageElement.naturalHeight,
        });

        console.log("UploadToolbar: Checking fabric environment", {
          Image: Image,
          ImagePrototype: Image.prototype,
          hasBringToFront: typeof Image.prototype.bringToFront === "function",
          fabricVersion: Image.version || "undefined",
          canvasMethods: canvas ? Object.keys(canvas) : null,
          canvasType: canvas ? canvas.constructor.name : "undefined",
        });
        if (!Image) {
          console.error("UploadToolbar: fabric.Image is not defined");
          setSnackbar({
            open: true,
            message: "Failed to add image. Please try refreshing.",
            severity: "error",
          });
          return;
        }

        try {
          const imgInstance = new Image(imageElement, {
            left: 0,
            top: 0,
            opacity: 1.0,
          });
          console.log("UploadToolbar: Created fabric.Image", {
            width: imgInstance.width,
            height: imgInstance.height,
            left: imgInstance.left,
            top: imgInstance.top,
            instanceMethods: Object.keys(imgInstance),
            hasBringToFront: typeof imgInstance.bringToFront === "function",
            prototype: Object.getPrototypeOf(imgInstance),
            instanceType: imgInstance.constructor.name,
          });

          // Scale to fit canvas
          const maxWidth = canvas.getWidth();
          const maxHeight = canvas.getHeight();
          const scale = Math.min(
            maxWidth / imgInstance.width,
            maxHeight / imgInstance.height,
            1
          );
          imgInstance.set({
            scaleX: scale,
            scaleY: scale,
          });
          console.log("UploadToolbar: Applied scaling", {
            scale,
            scaledWidth: imgInstance.width * scale,
            scaledHeight: imgInstance.height * scale,
            canvasWidth: canvas.getWidth(),
            canvasHeight: canvas.getHeight(),
            canvasScale,
          });

          canvas.add(imgInstance);
          canvas.setActiveObject(imgInstance);
          // Adjust stacking order
          try {
            if (typeof canvas.bringObjectToFront === "function") {
              canvas.bringObjectToFront(imgInstance);
              console.log("UploadToolbar: Used bringObjectToFront");
            } else {
              console.warn(
                "UploadToolbar: bringObjectToFront not available, attempting fallback"
              );
              // Fallback: Manual stack reordering
              const objects = canvas.getObjects();
              const index = objects.indexOf(imgInstance);
              if (index !== -1 && index < objects.length - 1) {
                objects.splice(index, 1);
                objects.push(imgInstance);
                canvas._objects = objects; // Update internal stack
                canvas.requestRenderAll();
                console.log("UploadToolbar: Manually reordered stack");
              } else {
                console.log("UploadToolbar: No reordering needed", {
                  index,
                  objectCount: objects.length,
                });
              }
            }
          } catch (stackError) {
            console.error(
              "UploadToolbar: Failed to adjust stacking order",
              stackError
            );
            setSnackbar({
              open: true,
              message: "Image added, but stacking order may be incorrect.",
              severity: "warning",
            });
          }
          canvas.requestRenderAll();
          console.log("UploadToolbar: Image added to canvas", {
            canvasObjects: canvas.getObjects().length,
            activeObject: canvas.getActiveObject()?.type,
            left: imgInstance.left,
            top: imgInstance.top,
            scaledWidth: imgInstance.width * scale,
            scaledHeight: imgInstance.height * scale,
            stackIndex: canvas.getObjects().indexOf(imgInstance),
          });
        } catch (error) {
          console.error("UploadToolbar: Failed to add image", error, {
            imageURLLength: imageURL?.length,
            canvasWidth: canvas.getWidth(),
            canvasHeight: canvas.getHeight(),
            canvasScale,
            canvasObjects: canvas.getObjects().length,
          });
          setSnackbar({
            open: true,
            message: "Error adding image: " + error.message,
            severity: "error",
          });
        }
      };
      imageElement.onerror = (error) => {
        console.error("UploadToolbar: Image element load failed", error);
        setSnackbar({
          open: true,
          message: "Error loading image file.",
          severity: "error",
        });
      };
      reader.onerror = (error) => {
        console.error("UploadToolbar: FileReader error", error);
        setSnackbar({
          open: true,
          message: "Error reading image file.",
          severity: "error",
        });
      };
    };
    reader.readAsDataURL(imgObj);
  };

  return (
    <>
      <Typography
        variant="h5"
        sx={{
          color: "#0d2445",
          fontFamily: "Roboto",
          fontWeight: 700,
          textAlign: "left",
        }}
      >
        Upload File
      </Typography>
      <Card
        sx={{
          backgroundColor: "white",
          color: "#0d2445",
          height: 80,
          width: 80,
          marginTop: 4,
          marginBottom: 1,
          marginLeft: 2,
          ":hover": { boxShadow: 10, backgroundColor: "#ffffff" },
        }}
      >
        <CardActionArea>
          <input
            accept="image/*"
            id="contained-button-file"
            type="file"
            style={{ display: "none" }}
            onChange={handleAddImage}
          />
          <label htmlFor="contained-button-file">
            <Button variant="text" component="div" elevation={4}>
              <Stack direction="column" alignItems="center">
                <UploadIcon />
                <Typography
                  variant="body1"
                  sx={{
                    color: "#0d2445",
                    fontSize: "12px",
                    fontFamily: "Roboto",
                    fontWeight: 500,
                  }}
                >
                  My Computer
                </Typography>
              </Stack>
            </Button>
          </label>
        </CardActionArea>
      </Card>
    </>
  );
};

export default UploadToolbar;
